// src/components/personal/analytics/DashboardSection/components/PerformanceChart.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TEXT_STYLES } from '@/theme';
import { Flex } from '@/components/ui/Layout';
import { ChartDataPoint, MetricType, Period } from '../../types/types';
import { 
  formatNumber, 
  formatPercent, 
  formatPrice, 
  formatCompactNumber 
} from '@/utils/Formatters';
import { 
  formatTime,
  isToday, 
  isYesterday 
} from '@/utils/dateUtils';
import { LOCALES, NUMBER_FORMATS } from '@/utils/constants';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

// Импортируем компоненты из recharts
import { 
  BarChart as RechartsBarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

interface PerformanceChartProps {
  data: ChartDataPoint[];
  metric: MetricType;
  period: Period;
  isLoading: boolean;
  periodSelector?: React.ReactNode;
  hideTitle?: boolean;
  dateRange?: { startDate: Date; endDate: Date };
}

// Вспомогательная функция для фильтрации данных по периоду
const filterDataByPeriod = (
  data: ChartDataPoint[], 
  period: Period, 
  dateRange?: { startDate: Date; endDate: Date }
): ChartDataPoint[] => {
  if (!data || data.length === 0) return [];

  console.log(`Фильтрация данных для периода ${period}`);
  
  // Для периода "day" - отображаем только часовые данные
  if (period === 'day') {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    // Фильтруем только данные с часами
    return data.filter(item => {
      // Если указан час, используем его
      if (item.hour !== undefined) {
        return hours.includes(Number(item.hour));
      }
      
      // Если имя в формате HH:00, извлекаем час
      if (item.name && item.name.includes(':')) {
        const hour = parseInt(item.name.split(':')[0], 10);
        return !isNaN(hour) && hours.includes(hour);
      }
      
      return false; // Отфильтровываем записи без информации о часе
    }).sort((a, b) => {
      // Сортируем по часам для обеспечения последовательности
      const hourA = a.hour !== undefined ? Number(a.hour) : 
                    (a.name && a.name.includes(':')) ? parseInt(a.name.split(':')[0], 10) : 0;
      const hourB = b.hour !== undefined ? Number(b.hour) : 
                    (b.name && b.name.includes(':')) ? parseInt(b.name.split(':')[0], 10) : 0;
      return hourA - hourB;
    });
  }
  
  // Для периода "month" - отображаем только дни текущего месяца
  if (period === 'month') {
    // Определяем текущий месяц и год
    let currentMonth, currentYear;
    
    if (dateRange) {
      currentMonth = dateRange.startDate.getMonth() + 1; // +1 потому что месяцы в JS начинаются с 0
      currentYear = dateRange.startDate.getFullYear();
    } else {
      const now = new Date();
      currentMonth = now.getMonth() + 1;
      currentYear = now.getFullYear();
    }
    
    console.log(`Фильтрация для месяца: ${currentMonth}, год: ${currentYear}`);
    
    // Фильтруем дни текущего месяца
    return data.filter(item => {
      // Если в данных есть месяц и год, проверяем соответствие
      if (item.month !== undefined && item.year !== undefined) {
        return Number(item.month) === currentMonth && Number(item.year) === currentYear;
      }
      
      // Если в данных есть только день, проверяем диапазон валидных дней
      if (item.day !== undefined) {
        const day = Number(item.day);
        // Дни должны быть в диапазоне 1-31
        return day >= 1 && day <= 31;
      }
      
      // Проверяем, может ли name быть днем месяца
      if (item.name && !isNaN(Number(item.name))) {
        const day = Number(item.name);
        return day >= 1 && day <= 31;
      }
      
      return false; // Отфильтровываем записи без информации о дне
    }).sort((a, b) => {
      // Сортируем по дням для обеспечения последовательности
      const dayA = a.day !== undefined ? Number(a.day) : 
                   (a.name && !isNaN(Number(a.name))) ? Number(a.name) : 0;
      const dayB = b.day !== undefined ? Number(b.day) : 
                   (b.name && !isNaN(Number(b.name))) ? Number(b.name) : 0;
      return dayA - dayB;
    });
  }
  
  // Для периода "year" - отображаем только месяцы
  if (period === 'year') {
    // Проверяем наличие месячных данных
    const hasMonthData = data.some(item => 
      item.month !== undefined && (item.day === undefined || item.hour === undefined));
    
    console.log('Наличие месячных данных:', hasMonthData);
    
    // Если нет данных в месячном формате, фильтруем по имени (предполагаем, что имена - названия месяцев)
    if (!hasMonthData) {
      return data.filter(item => item.name && isNaN(Number(item.name)));
    }
    
    // Фильтруем только месячные данные
    return data.filter(item => {
      // Данные должны иметь месяц, но не иметь день или час
      return item.month !== undefined && (item.day === undefined || item.hour === undefined);
    }).sort((a, b) => {
      // Сортируем по месяцам для обеспечения последовательности
      const monthA = a.month !== undefined ? Number(a.month) : 0;
      const monthB = b.month !== undefined ? Number(b.month) : 0;
      return monthA - monthB;
    });
  }
  
  // Для произвольного периода - используем только даты, строго в диапазоне
  if (period === 'custom' && dateRange) {
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;
    
    return data.filter(item => {
      // Если в данных есть полная дата (год, месяц, день)
      if (item.year !== undefined && item.month !== undefined && item.day !== undefined) {
        const itemDate = new Date(Number(item.year), Number(item.month) - 1, Number(item.day));
        return itemDate >= startDate && itemDate <= endDate;
      }
      
      return true; // По умолчанию оставляем данные, если нет информации о дате
    });
  }
  
  // По умолчанию возвращаем данные без фильтрации
  return data;
};

const PerformanceChart: React.FC<PerformanceChartProps> = ({ 
  data, 
  metric,
  period,
  isLoading,
  periodSelector,
  hideTitle = false,
  dateRange
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Применяем фильтрацию данных в зависимости от периода
  const filteredData = useMemo(() => {
    console.log('PerformanceChart - Период:', period);
    console.log('PerformanceChart - Количество данных до фильтрации:', data?.length || 0);
    
    if (dateRange) {
      console.log('PerformanceChart - Диапазон дат:', {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString()
      });
    }
    
    const filtered = filterDataByPeriod(data, period, dateRange);
    
    console.log('PerformanceChart - Количество данных после фильтрации:', filtered?.length || 0);
    if (filtered.length > 0) {
      console.log('PerformanceChart - Первый элемент после фильтрации:', filtered[0]);
    }
    return filtered;
  }, [data, period, dateRange]);
  
  // Проверка наличия данных
  const noDataAvailable = filteredData.length === 0 || 
                          filteredData.every(item => 
                            (item[metric] === 0 || item[metric] === undefined) && 
                            item.revenue === 0 && 
                            item.orders === 0);
  
  // Карта цветов для различных метрик
  const metricColors: Record<MetricType, string> = {
    revenue: '#245D33',
    averageCheck: '#2E7D32',
    orders: '#1976D2',
    clients: '#7B1FA2',
    conversion: '#C62828'
  };
  
  // Определяем массив для фиксированных временных меток в дневном режиме
  const dayTimeTicks = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'];
  
  // Функция проверки, является ли значение одним из фиксированных тиков для дня
  
  // Функция форматирования тиков оси X с учетом периода и локализации
  const formatXAxisTick = (value: string, index: number) => {
    switch (period) {
      case 'day':
        // Для дня показываем все метки часов
        return formatTime(value, currentLocale);
      case 'week':
        // Для недели показываем сокращенные дни недели
        return value;
      case 'month': {
        // Для месяца показываем только воскресенья и применяем умное форматирование
        const item = filteredData[index];
        if (!item?.isSunday) return '';
        
        // Создаем дату из дня месяца для проверки сегодня/вчера
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const dateObj = new Date(year, month, parseInt(value));
        
        // Преобразуем в ISO формат для наших утилит
        const isoDate = dateObj.toISOString();
        
        // Используем умное форматирование даты
        if (isToday(isoDate)) return t('formats.today');
        if (isYesterday(isoDate)) return t('formats.yesterday');
        
        // Для других дат показываем просто день
        return value;
      }
      case 'quarter':
        // Для квартала добавляем префикс "W" к номеру недели
        return `W${value}`;
      case 'year':
        // Для года используем короткие названия месяцев, которые уже отформатированы
        return value;
      case 'custom':
        // Для произвольного периода показываем все метки
        return value;
      default:
        return value;
    }
  };
  
  // Функция форматирования тиков оси Y в зависимости от типа метрики
  const formatYAxisTick = (value: number) => {
    // Для больших чисел используем компактное форматирование
    if (value >= 1000) {
      return formatCompactNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION);
    }
    
    // Для остальных используем обычное форматирование чисел
    return formatNumber(value, undefined, true, currentLocale);
  };
  
  // Функция форматирования значения для tooltip в зависимости от типа метрики
  const formatTooltipValue = (value: number): string => {
    if (metric === 'conversion') {
      return formatPercent(value / 100, NUMBER_FORMATS.PERCENT_PRECISION, true, currentLocale);
    } else if (metric === 'revenue' || metric === 'averageCheck') {
      return formatPrice(value, currencySymbol, currentLocale);
    }
    return formatNumber(value, undefined, true, currentLocale);
  };

  return (
    <div className="relative overflow-hidden rounded-lg shadow-sm border border-[#e5e7eb] bg-white">
      {!hideTitle && (
        <div className="bg-[#F5FAF5] px-5 py-3 border-b border-[#e5e7eb]">
          <Flex justify="between" align="center">
            <div>
              <h2 className={TEXT_STYLES.h2}>{t(`charts.${metric}.title`)}</h2>
              <p className="text-gray-500">{t('charts.subtitle')}</p>
            </div>
            {periodSelector && (
              <div className="ml-auto">
                {periodSelector}
              </div>
            )}
          </Flex>
        </div>
      )}
      <div className="p-3">
        <div className="h-[500px] md:h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={filteredData}
              barSize={period === 'month' ? 6 : 20}
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
              width={filteredData.length * 30}
              height={450}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fill: '#465357' }}
                tickFormatter={formatXAxisTick}
                height={50}
                ticks={period === 'day' ? dayTimeTicks : undefined}
              />
              <YAxis 
                tick={{ fill: '#465357' }} 
                tickFormatter={formatYAxisTick}
                label={{ 
                  value: metric === 'conversion' ? '%' : '', 
                  angle: -90, 
                  position: 'insideLeft' 
                }}
                width={60}
              />
              <Tooltip 
                content={(tooltipProps) => {
                  const { active, payload, label } = tooltipProps;
                  
                  if (!active || !payload || !payload.length) {
                    return null;
                  }
                  
                  const data = payload[0].payload;
                  const value = payload[0].value;
                  
                  // Форматируем значение в зависимости от типа метрики
                  const formattedValue = value !== undefined 
                    ? formatTooltipValue(Number(value))
                    : '0';
                  
                  // Формируем текст для отображения в зависимости от периода
                  let displayLabel;
                  switch (period) {
                    case 'day':
                      displayLabel = data.displayName || `${label}:00`;
                      break;
                    case 'week':
                      displayLabel = data.displayName || label;
                      break;
                    case 'month':
                      displayLabel = t('tooltip.day', { day: data.displayName || label });
                      break;
                    case 'quarter':
                      displayLabel = t('tooltip.week', { week: data.displayName || label });
                      break;
                    case 'year':
                      // Для годового периода используем отображаемое имя (полное название месяца)
                      displayLabel = data.displayName || label;
                      break;
                    case 'custom':
                      // Для произвольного периода используем день и месяц
                      displayLabel = data.displayName || label;
                      break;
                    default:
                      displayLabel = data.displayName || label;
                  }
                  
                  return (
                    <div className="bg-white p-2 border border-[#A7ABAA] rounded-lg shadow-md text-sm">
                      <div className="font-medium mb-1 pb-1 border-b border-[#A7ABAA]/20">
                        {displayLabel}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 mr-2 rounded-sm" 
                            style={{ backgroundColor: payload[0].color }}
                          ></div>
                          <span className="mr-2">{t(`metrics.${metric}`)}:</span>
                          <span className="font-medium">{formattedValue}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              {/* Настраиваем бар для отображения данных */}
              <Bar
                dataKey={metric}
                name={t(`metrics.${metric}`)}
                fill={metricColors[metric]}
                radius={[4, 4, 0, 0]}
              />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center flex-col">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#245D33]"></div>
          <p className="mt-2 text-gray-600">{t('loading')}</p>
        </div>
      )}
      {noDataAvailable && !isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center flex-col">
          <p className="text-gray-600">{t('no_data_for_period', 'Нет данных за выбранный период')}</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceChart;